/**
 * Membership funnel (dual / Stripe / post-pago) — always English UI.
 * Portal + PortalCompleteProfile already use inline EN; this covers the rest.
 */

import type { RegistrationData } from "@/lib/auth";
import { MIN_REGISTRATION_AGE, isAdultBirthDate } from "@/lib/auth";

/** Paths where site + Clerk must render in English regardless of geo. */
export function isMembershipFunnelPath(pathname: string): boolean {
  if (
    pathname === "/registro" ||
    pathname === "/entrar" ||
    pathname === "/completar-registro" ||
    pathname === "/pending-approval" ||
    pathname === "/mi-acceso" ||
    pathname === "/portal-acceso"
  ) {
    return true;
  }
  return pathname === "/portal" || pathname.startsWith("/portal/");
}

export function buildMembershipFunnelQuery(
  parts: Record<string, string | undefined>,
): string {
  const sp = new URLSearchParams();
  sp.set("lang", "en");
  for (const [key, value] of Object.entries(parts)) {
    const trimmed = value?.trim();
    if (trimmed) sp.set(key, trimmed);
  }
  return `?${sp.toString()}`;
}

export const MEMBERSHIP_PORTAL_HINT_EN = "Access steps guide";

export const REGISTRO_FUNNEL_COPY = {
  seoTitle: "Create your account",
  seoDescription: (brand: string) => `Sign up to access your ${brand} membership.`,
  signUpTitle: "Create your account",
  signInTitle: "Welcome back",
  subtitleAccessRequest: (brand: string) =>
    `Create your account to request VIP access to ${brand}. The team will review your request.`,
  subtitlePaidVip: (brand: string) =>
    `Thanks for your purchase! Finish setting up your ${brand} membership.`,
  subtitlePaidBasic: (brand: string) =>
    `Thanks for your purchase! Create your account and complete your profile to activate group access.`,
  subtitlePaidStandard: (brand: string) =>
    `Thanks for your purchase! Create your account to activate your Standard membership with ${brand}.`,
  subtitleDefault: (brand: string) =>
    `Create an account to access your ${brand} membership.`,
  subtitleSignIn: (brand: string) => `Sign in to access ${brand}.`,
  badgeFreeTitle: "Free access:",
  badgeFreeBody:
    "Create your account, complete a quick profile, and the team will email your access link.",
  badgeAccessRequestTitle: "After you create your account",
  badgeAccessRequestBody:
    "the team will review your VIP access request and, if approved, assign your private link (usually within a few hours).",
  badgePaidPrefix: "Your payment was received.",
  badgePaidBasicSuffix:
    " After you create your account, the team will validate your account and send the right access link.",
  badgePaidVipSuffix:
    " After you create your account, the team will assign your private Telegram link (may take a few hours).",
  approvalFooter:
    "Access to the private group/link requires manual team approval.",
} as const;

export const COMPLETAR_REGISTRO_FUNNEL_COPY = {
  seoTitle: "Complete your profile",
  seoDescription: (brand: string) =>
    `Complete your profile to access your ${brand} membership.`,
  title: "Almost there!",
  descFree:
    "Fill in these details so the team can email your free access link.",
  descBasicStandard:
    "Fill in these details so the team can validate your group access and send the right link.",
  descDefault:
    "Complete your profile so the team can assign your private access link.",
  aliasLabel: "Nickname or alias",
  aliasPlaceholder: "What should we call you?",
  aliasHint: "Between 3 and 30 characters",
  telegramLabel: "Telegram",
  telegramPlaceholder: "@username or +1...",
  telegramHint: "Required so we can add you to the private group.",
  birthDateLabel: "Date of birth",
  birthDateHint: (age: number) => `${age}+ years`,
  saveButton: "Save and continue",
  saving: "Saving...",
  openingCheckout: "Opening checkout…",
  saveError: "Couldn't save your profile.",
  noticeFree:
    "After saving, the team will review your details and email your access link.",
  noticeVip:
    "After saving, the team will assign your private access link (may take a few hours).",
  noticePaid:
    "After saving, the team will validate your account and send your access link when ready.",
} as const;

export function validateRegistrationDataEn(input: RegistrationData): string | null {
  const alias = (input.alias ?? "").trim();
  const telegram = (input.telegram ?? "").trim();
  const birthDate = (input.birthDate ?? "").trim();
  if (alias.length < 3 || alias.length > 30) {
    return "Nickname must be between 3 and 30 characters.";
  }
  if (telegram.length < 3 || telegram.length > 50) {
    return "Telegram username must be between 3 and 50 characters.";
  }
  if (!birthDate) {
    return "Enter your date of birth.";
  }
  if (!isAdultBirthDate(birthDate)) {
    return `You must be at least ${MIN_REGISTRATION_AGE} years old to continue.`;
  }
  return null;
}